/*
 * Context for managing shipping and billing addresses across checkout components
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import PropTypes from 'prop-types'

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'
import {compareAddresses} from './addresses/shared/form-constants'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'

const CheckoutAddressesContext = createContext()

export const CheckoutAddressesProvider = ({children}) => {
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()
    const {data: customer, isLoading} = useCurrentCustomer()
    const {site} = useMultiSite()

    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null)
    const [selectedBillingAddress, setSelectedBillingAddress] = useState(null)
    const [billingSameAsShipping, setBillingSameAsShipping] = useState(true)

    const [savedShippingAddress, setSavedShippingAddress] = useState(null)
    const [savedBillingAddress, setSavedBillingAddress] = useState(null)

    const [hasInitialized, setHasInitialized] = useState(false)
    const [lastCustomerKey, setLastCustomerKey] = useState(null)

    const isRegisteredCustomerLoading = isLoading && customer?.isRegistered

    const prevLocaleRef = useRef(null)
    // SYNC: Load saved addresses from basket (only once on init)
    useEffect(() => {
        if (!basket || isRegisteredCustomerLoading) return

        // If user is registered but customer data not loaded yet, wait
        // Only wait for customer if user is actually registered (has customerId)
        // Don't wait for guest users - they either have customer.isGuest = true or have email but no customerId
        const hasCustomerId = basket?.customerInfo?.customerId
        const hasEmail = basket?.customerInfo?.email
        const isGuestUser = customer?.isGuest || (hasEmail && !hasCustomerId)
        const isWaitingForRegisteredCustomer =
            hasCustomerId && !customer?.customerId && !isGuestUser

        const isDefinitelyGuest = customer?.isGuest === true || (!hasCustomerId && hasEmail)
        if (isWaitingForRegisteredCustomer && !isDefinitelyGuest) return

        const customerKey = `${customer?.isGuest}-${customer?.customerId || 'none'}`

        if (hasInitialized && customerKey === lastCustomerKey) return

        const basketShipping = basket?.shipments?.[0]?.shippingAddress
        const basketBilling = basket?.billingAddress

        setSavedShippingAddress(basketShipping)
        setSavedBillingAddress(basketBilling)

        const isBasketShippingValid = basketShipping && Object.keys(basketShipping).length > 1

        if (isBasketShippingValid) {
            setSelectedShippingAddress(basketShipping)
        } else if (customer?.addresses?.length > 0) {
            const preferredAddress = customer.addresses.find((addr) => addr.preferred === true)
            const defaultAddress = preferredAddress || customer.addresses[0]
            setSelectedShippingAddress(defaultAddress)
        } else {
            setSelectedShippingAddress(undefined)
        }

        const isBasketBillingValid = basketBilling && Object.keys(basketBilling).length > 1

        if (isBasketBillingValid) {
            setSelectedBillingAddress(basketBilling)
        } else if (customer?.addresses?.length > 0) {
            const preferredBillingAddress = customer?.addresses?.find(
                (addr) => addr.c_isBillingAddress
            )
            const preferredBillingDefaultAddress = customer.addresses.find(
                (addr) => addr.preferred === true
            )
            const defaultBillingAddress =
                preferredBillingAddress || preferredBillingDefaultAddress || customer.addresses[0]
            setSelectedBillingAddress(defaultBillingAddress)
        }

        if (!hasInitialized) {
            if (basketShipping && basketBilling) {
                const addressesMatch = compareAddresses(basketShipping, basketBilling)
                setBillingSameAsShipping(addressesMatch)
            } else if (basketShipping && !basketBilling) {
                setBillingSameAsShipping(true)
            }
        }

        setLastCustomerKey(customerKey)
        setHasInitialized(true)
    }, [basket, customer, hasInitialized, site, lastCustomerKey, isRegisteredCustomerLoading])

    // Force initialization for guest users if it's been too long
    useEffect(() => {
        if (hasInitialized) return

        // If we have a basket with email but no customerId, it's likely a guest
        const hasBasketWithEmail = basket?.customerInfo?.email && !basket?.customerInfo?.customerId
        const isDefinitelyGuest = customer?.isGuest === true

        if (hasBasketWithEmail || isDefinitelyGuest) {
            setHasInitialized(true)
        }
    }, [basket?.customerInfo, customer?.isGuest, hasInitialized])

    const selectShippingAddress = useCallback(
        (address) => {
            setSelectedShippingAddress(address)

            if (billingSameAsShipping) {
                setSelectedBillingAddress(address)
            }
        },
        [billingSameAsShipping]
    )

    const selectBillingAddress = useCallback((address) => {
        setSelectedBillingAddress(address)
    }, [])

    const toggleBillingSameAsShipping = useCallback(
        (isSame) => {
            setBillingSameAsShipping(isSame)

            if (selectedShippingAddress) {
                setSelectedBillingAddress(selectedShippingAddress)
            }
        },
        [selectedShippingAddress]
    )

    const syncAfterSave = useCallback(async () => {
        const freshBasket = await refetchBasket()
        const basketShipping = freshBasket.data?.shipments?.[0]?.shippingAddress
        const basketBilling = freshBasket.data?.billingAddress

        setSavedShippingAddress(basketShipping)
        setSavedBillingAddress(basketBilling)

        if (billingSameAsShipping && basketShipping) {
            setSelectedBillingAddress(basketShipping)
        }
    }, [refetchBasket, billingSameAsShipping])

    const resetAndReinitialize = useCallback(() => {
        setSelectedShippingAddress(null)
        setSelectedBillingAddress(null)
        setBillingSameAsShipping(true)
        setSavedShippingAddress(null)
        setSavedBillingAddress(null)
        setHasInitialized(false)

        setTimeout(() => {
            setHasInitialized(true)
        }, 300)
    }, [])

    const refreshAddressData = useCallback(() => {
        resetAndReinitialize()
    }, [])

    useEffect(() => {
        if (!hasInitialized || typeof window === 'undefined') return

        const currentLocale = window.location.pathname.split('/')[1] || 'nl'

        if (prevLocaleRef.current && prevLocaleRef.current !== currentLocale) {
            resetAndReinitialize()
        }

        prevLocaleRef.current = currentLocale
    }, [
        typeof window !== 'undefined' ? window.location.pathname : '',
        hasInitialized,
        resetAndReinitialize
    ])

    const value = useMemo(
        () => ({
            // Selected addresses (UI state)
            selectedShippingAddress,
            selectedBillingAddress,
            billingSameAsShipping,

            // Saved addresses (backend state)
            savedShippingAddress,
            savedBillingAddress,

            // UI actions (no API calls)
            selectShippingAddress,
            selectBillingAddress,
            toggleBillingSameAsShipping,

            // Sync after save
            syncAfterSave,

            // Reset and reinitialize context
            resetAndReinitialize,

            // Cache invalidation

            // Refresh address data
            refreshAddressData,

            // State flags
            hasInitialized,

            // Raw data for compatibility
            basket,
            customer
        }),
        [
            selectedShippingAddress,
            selectedBillingAddress,
            billingSameAsShipping,
            savedShippingAddress,
            savedBillingAddress,
            selectShippingAddress,
            selectBillingAddress,
            toggleBillingSameAsShipping,
            syncAfterSave,
            resetAndReinitialize,
            refreshAddressData,
            hasInitialized,
            basket,
            customer
        ]
    )

    return (
        <CheckoutAddressesContext.Provider value={value}>
            {children}
        </CheckoutAddressesContext.Provider>
    )
}

CheckoutAddressesProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const useCheckoutAddresses = () => {
    const context = useContext(CheckoutAddressesContext)
    if (!context) {
        throw new Error('useCheckoutAddresses must be used within CheckoutAddressesProvider')
    }
    return context
}
